import { Routes, Route, Navigate } from "react-router-dom";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import UgcRulesPage from "@/pages/UgcRulesPage";
import DmcaPage from "@/pages/DmcaPage";
import RefundsPage from "@/pages/RefundsPage";
import "@/App.css";
import { Layout } from "@/components/Layout";
import { PrintsLayout } from "@/components/PrintsLayout";
import { useAuth } from "@/context/AuthContext";
import PortalPage from "@/pages/PortalPage";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import StorePage from "@/pages/StorePage";
import CollectionPage from "@/pages/CollectionPage";
import CardDetailPage from "@/pages/CardDetailPage";
import RedeemPage from "@/pages/RedeemPage";
// TradesPage removed — all trading now flows through the Request Board (/requests).
import RequestsPage from "@/pages/RequestsPage";
import ProfilePage from "@/pages/ProfilePage";
import ExplorePage from "@/pages/ExplorePage";
import ArenaPage from "@/pages/ArenaPage";
import BattlePage from "@/pages/BattlePage";
import SpectatorMatchPage from "@/pages/SpectatorMatchPage";
import RaceLobbyPage from "@/pages/RaceLobbyPage";
import RaceTrackPage from "@/pages/RaceTrackPage";
import RaceSpectatorPage from "@/pages/RaceSpectatorPage";
import WaifuForgePage from "@/pages/WaifuForgePage";
import AscendPage from "@/pages/AscendPage";
import PathPage from "@/pages/PathPage";
import CavernPage from "@/pages/CavernPage";
import AdventurePage from "@/pages/AdventurePage";
import CavernRoom from "@/pages/CavernRoom";
import TalentShowPage from "@/pages/TalentShowPage";
import TalentShowRoom from "@/pages/TalentShowRoom";
import CreditReturnPage from "@/pages/CreditReturnPage";
import AdminPage from "@/pages/admin/AdminPage";
import HelpPage from "@/pages/HelpPage";
import PrintsHomePage from "@/pages/PrintsHomePage";
import PrintsQuotePage from "@/pages/PrintsQuotePage";
import PrintsShopPage from "@/pages/PrintsShopPage";
import ChangelogPage from "@/pages/ChangelogPage";
import RoadmapPage from "@/pages/RoadmapPage";
import DeepDivePage from "@/pages/DeepDivePage";
import HousesPage from "@/pages/HousesPage";
import HouseDetailPage from "@/pages/HouseDetailPage";
import { GuildsPage, GuildDetailPage } from "@/pages/GuildsPage";
import { AdminPinGate } from "@/components/AdminPinGate";

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function App() {
  const { user, pinOk } = useAuth();
  const showPinGate = user?.role === "admin" && !pinOk;
  return (
    <div className="App dark min-h-screen relative">
      <div className="ambient-bg" />
      <div className="ambient-scanlines" />
      <div className="ambient-noise" />
      {showPinGate && <AdminPinGate />}
      <div className="relative z-10">
        <Routes>
          {/* Front door — chooses between Playden RPG and Playden 3D Printing.
              No Layout wrapper, no age gate — this is a neutral splash. */}
          <Route path="/" element={<PortalPage />} />

          {/* Playden 3D Printing side — its own light layout, no age gate by
              default (admin can force it via Portal settings). */}
          <Route path="/prints" element={<PrintsLayout />}>
            <Route index element={<PrintsHomePage />} />
            <Route path="shop" element={<PrintsShopPage />} />
            <Route path="quote" element={<PrintsQuotePage />} />
            <Route path="changelog" element={<ChangelogPage />} />
            {/* Future prints subpages plug in here — keep them under /prints/*. */}
            <Route path="*" element={<Navigate to="/prints" replace />} />
          </Route>

          {/* Playden RPG side — everything else. Full nav + age gate + how-to
              gate + ToS gate all live inside Layout. */}
          <Route element={<Layout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/deep-dive" element={<DeepDivePage />} />
            <Route path="/houses" element={<PrivateRoute><HousesPage /></PrivateRoute>} />
            <Route path="/houses/:key" element={<PrivateRoute><HouseDetailPage /></PrivateRoute>} />
            <Route path="/guilds" element={<PrivateRoute><GuildsPage /></PrivateRoute>} />
            <Route path="/guilds/:id" element={<PrivateRoute><GuildDetailPage /></PrivateRoute>} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/legal/ugc-rules" element={<UgcRulesPage />} />
            <Route path="/legal/dmca" element={<DmcaPage />} />
            <Route path="/legal/refunds" element={<RefundsPage />} />
            <Route path="/card/:id" element={<CardDetailPage />} />
            <Route path="/collection" element={<PrivateRoute><CollectionPage /></PrivateRoute>} />
            <Route path="/redeem" element={<PrivateRoute><RedeemPage /></PrivateRoute>} />
            <Route path="/trades" element={<Navigate to="/requests" replace />} />
            <Route path="/arena" element={<PrivateRoute><ArenaPage /></PrivateRoute>} />
            <Route path="/arena/battle/:id" element={<PrivateRoute><BattlePage /></PrivateRoute>} />
            <Route path="/arena/watch/:id" element={<PrivateRoute><SpectatorMatchPage /></PrivateRoute>} />
            <Route path="/race" element={<PrivateRoute><RaceLobbyPage /></PrivateRoute>} />
            <Route path="/race/watch/:id" element={<PrivateRoute><RaceSpectatorPage /></PrivateRoute>} />
            <Route path="/race/:id" element={<PrivateRoute><RaceTrackPage /></PrivateRoute>} />
            <Route path="/upgrade" element={<PrivateRoute><WaifuForgePage /></PrivateRoute>} />
            <Route path="/ascend" element={<PrivateRoute><AscendPage /></PrivateRoute>} />
            <Route path="/path" element={<PrivateRoute><PathPage /></PrivateRoute>} />
            <Route path="/cavern" element={<PrivateRoute><CavernPage /></PrivateRoute>} />
            <Route path="/cavern/:id" element={<PrivateRoute><CavernRoom /></PrivateRoute>} />
            <Route path="/adventure" element={<PrivateRoute><AdventurePage /></PrivateRoute>} />
            <Route path="/talentshow" element={<TalentShowPage />} />
            <Route path="/talentshow/:showId" element={<TalentShowRoom />} />
            <Route path="/credits/return" element={<PrivateRoute><CreditReturnPage /></PrivateRoute>} />
            <Route path="/requests" element={<PrivateRoute><RequestsPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
            <Route path="/admin/*" element={<PrivateRoute adminOnly><AdminPage /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </div>
    </div>
  );
}

export default App;
